import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { onboardingApi } from "../../../api/onboardingApi";
import { useNotifications } from "../../../context/NotificationContext";
import Modal from "../../../components/common/modals/Modal";
import {
  AlertTriangle,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import "./RequestList.css";

const REQUEST_STEP_ORDER = [
  "package",
  "business",
  "domain",
  "design",
  "products",
  "agreement",
  "invoice",
];

const parseRequirements = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "Not provided";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const displayLabel = (value) =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const SubmissionSection = ({ title, icon, entries }) => {
  const visibleEntries = entries.filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0),
  );

  return (
    <section className="submission-section">
      <h3>
        {icon && <i className={`fas ${icon}`} aria-hidden="true" />}
        {title}
      </h3>
      {visibleEntries.length ? (
        <dl className="submission-details-grid">
          {visibleEntries.map(([label, value]) => (
            <div className="submission-detail-item" key={label}>
              <dt>{label}</dt>
              <dd>{formatValue(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="submission-empty">No details were saved for this step.</p>
      )}
    </section>
  );
};

const RequestList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    search: "",
  });
  const [submissionModal, setSubmissionModal] = useState({
    open: false,
    loading: false,
    error: "",
    request: null,
    steps: [],
  });
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [openActionsRequestId, setOpenActionsRequestId] = useState(null);
  const [actionError, setActionError] = useState("");
  const navigate = useNavigate();
  const { socket } = useNotifications();

  useEffect(() => {
    if (!openActionsRequestId) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!event.target.closest?.("[data-request-actions]")) {
        setOpenActionsRequestId(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenActionsRequestId(null);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openActionsRequestId]);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await storeRequestApi.getAllRequests(filters);
      setRequests(response.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRequests();
    const refreshInterval = setInterval(fetchRequests, 30000);
    return () => clearInterval(refreshInterval);
  }, [fetchRequests]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("request:updated", fetchRequests);
    socket.on("platform:updated", fetchRequests);
    return () => {
      socket.off("request:updated", fetchRequests);
      socket.off("platform:updated", fetchRequests);
    };
  }, [fetchRequests, socket]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name === "status") {
      setSearchParams({ status: value });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRequests();
  };

  const getStatusBadge = (status) => {
    return `status-badge status-${status}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const openSubmissionModal = async (requestId) => {
    setSubmissionModal({
      open: true,
      loading: true,
      error: "",
      request: null,
      steps: [],
    });
    try {
      const [requestResponse, stepsResponse] = await Promise.all([
        storeRequestApi.getRequestById(requestId),
        onboardingApi.getStoreSteps(requestId),
      ]);
      setSubmissionModal({
        open: true,
        loading: false,
        error: "",
        request: requestResponse.data,
        steps: Array.isArray(stepsResponse.data) ? stepsResponse.data : [],
      });
    } catch (error) {
      console.error("Error loading client submission:", error);
      setSubmissionModal((current) => ({
        ...current,
        loading: false,
        error:
          error.response?.data?.error ||
          "Could not load the client's submitted steps. Please try again.",
      }));
    }
  };

  const manageRequest = (requestId) => {
    setSubmissionModal((current) => ({ ...current, open: false }));
    setOpenActionsRequestId(null);
    navigate(`/admin/requests/${requestId}`);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;
    const requestId = requestToDelete.StoreRequestID;
    setDeletingRequestId(requestId);
    setActionError("");
    try {
      await storeRequestApi.deleteRequest(requestId);
      setRequests((current) =>
        current.filter((request) => request.StoreRequestID !== requestId),
      );
      setSubmissionModal((current) =>
        current.request?.StoreRequestID === requestId
          ? { ...current, open: false, request: null }
          : current,
      );
      setRequestToDelete(null);
    } catch (deleteError) {
      setActionError(
        deleteError.response?.data?.error ||
          "Could not permanently delete this store request.",
      );
    } finally {
      setDeletingRequestId(null);
    }
  };

  useEffect(() => {
    if (!submissionModal.open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setSubmissionModal((current) => ({ ...current, open: false }));
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [submissionModal.open]);

  const renderSubmissionModal = () => {
    if (!submissionModal.open) return null;
    const request = submissionModal.request;
    const requirements = parseRequirements(request?.AdditionalRequirements);
    const businessInfo = requirements.businessInfo || {};
    const packageInfo = requirements.package || {};
    const domainInfo = requirements.domain || {};
    const designInfo = requirements.design || {};
    const agreementInfo = requirements.agreement || {};
    const invoiceInfo = requirements.invoice || {};
    const wizardSteps = submissionModal.steps
      .filter((step) =>
        REQUEST_STEP_ORDER.includes(
          String(step.StepKey || "").toLowerCase(),
        ),
      )
      .sort(
        (first, second) =>
          REQUEST_STEP_ORDER.indexOf(String(first.StepKey).toLowerCase()) -
          REQUEST_STEP_ORDER.indexOf(String(second.StepKey).toLowerCase()),
      );
    const productEntries = Array.isArray(requirements.products)
      ? requirements.products.map((product, index) => [
          `Product ${index + 1}`,
          [
            product.name,
            product.price ? `R${product.price}` : "",
            product.stock ? `Stock: ${product.stock}` : "",
            product.category,
            product.description,
            product.imageFileName ? `Image: ${product.imageFileName}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
        ])
      : [];

    return (
      <div
        className="submission-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setSubmissionModal((current) => ({ ...current, open: false }));
          }
        }}
      >
        <section
          className="submission-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="submission-modal-title"
        >
          <header className="submission-modal-header">
            <div>
              <span className="submission-modal-eyebrow">Client submission</span>
              <h2 id="submission-modal-title">
                {request?.BusinessName ||
                  request?.ClientBusinessName ||
                  "Store request"}
              </h2>
              {request && (
                <p>
                  {request.RequestNumber || "Request"} ·{" "}
                  {request.PackageName || "Package not available"}
                </p>
              )}
            </div>
            <div className="submission-modal-header-actions">
              {request && (
                <button
                  type="button"
                  className="submission-manage-quick-access"
                  onClick={() => manageRequest(request.StoreRequestID)}
                >
                  <ExternalLink size={15} />
                  Manage request
                </button>
              )}
              <button
                type="button"
                className="submission-modal-close"
                aria-label="Close submission details"
                onClick={() =>
                  setSubmissionModal((current) => ({
                    ...current,
                    open: false,
                  }))
                }
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </header>

          <div className="submission-modal-body">
            {submissionModal.loading ? (
              <div className="submission-modal-loading">
                <div className="spinner" />
                <p>Loading submitted steps…</p>
              </div>
            ) : submissionModal.error ? (
              <div className="submission-modal-error" role="alert">
                {submissionModal.error}
              </div>
            ) : request ? (
              <>
                <section className="submission-progress-section">
                  <h3>Client onboarding steps</h3>
                  {wizardSteps.length ? (
                    <ol className="submission-step-list">
                      {wizardSteps.map((step) => {
                        const completed =
                          String(step.ProgressStatus).toLowerCase() ===
                          "completed";
                        return (
                          <li
                            className={completed ? "completed" : ""}
                            key={step.OnboardingStepID}
                          >
                            <span className="submission-step-icon">
                              {completed ? "✓" : step.StepNumber}
                            </span>
                            <span>{step.Name}</span>
                            <strong>
                              {completed
                                ? "Submitted"
                                : displayLabel(step.ProgressStatus || "pending")}
                            </strong>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <p className="submission-empty">
                      No configured wizard steps were returned.
                    </p>
                  )}
                </section>

                <SubmissionSection
                  title="1. Package"
                  icon="fa-box"
                  entries={[
                    ["Package", packageInfo.name || request.PackageName],
                    ["Tier", packageInfo.tier || request.Tier],
                    [
                      "Plan price",
                      packageInfo.price !== undefined
                        ? `R${Number(packageInfo.price).toFixed(2)} / ${packageInfo.billingPeriod || "month"}`
                        : request.Price !== undefined
                          ? `R${Number(request.Price).toFixed(2)}`
                          : "",
                    ],
                    [
                      "Setup fee",
                      packageInfo.setupFee !== undefined
                        ? `R${Number(packageInfo.setupFee).toFixed(2)}`
                        : "",
                    ],
                  ]}
                />
                <SubmissionSection
                  title="2. Business details"
                  icon="fa-building"
                  entries={[
                    ["Business name", businessInfo.businessName || request.BusinessName],
                    ["Business type", businessInfo.businessType || request.BusinessCategory],
                    ["Contact name", businessInfo.contactName],
                    ["Email", businessInfo.email || request.Email],
                    ["Phone", businessInfo.phone || request.MobileNumber],
                    ["Current website", businessInfo.website],
                    ["Business description", businessInfo.description || request.BusinessDescription],
                  ]}
                />
                {requirements.onboardingAssessment && (
                  <SubmissionSection
                    title="Business readiness"
                    icon="fa-lightbulb"
                    entries={Object.entries(requirements.onboardingAssessment).map(
                      ([key, value]) => [displayLabel(key), value],
                    )}
                  />
                )}
                <SubmissionSection
                  title="3. Domain"
                  icon="fa-globe"
                  entries={[
                    ["Domain choice", domainInfo.domainType],
                    [
                      "Domain",
                      domainInfo.domainType === "subdomain"
                        ? `${domainInfo.subdomain || ""}.tooraflex.co.za`
                        : domainInfo.domainType === "existing"
                          ? domainInfo.existingDomain
                          : `${domainInfo.customDomain || ""}${domainInfo.selectedExtension || ""}`,
                    ],
                    ["Availability checked", domainInfo.availabilityChecked],
                    [
                      "Availability",
                      domainInfo.availability?.available === true
                        ? "Available"
                        : domainInfo.availability?.available === false
                          ? "Not available"
                          : "",
                    ],
                    [
                      "Domain total",
                      domainInfo.domainPrice !== undefined
                        ? `R${Number(domainInfo.domainPrice).toFixed(2)} / year`
                        : "",
                    ],
                  ]}
                />
                <SubmissionSection
                  title="4. Store design"
                  icon="fa-palette"
                  entries={[
                    ["Template", designInfo.template],
                    ["Primary color", designInfo.primaryColor],
                    ["Secondary color", designInfo.secondaryColor],
                    ["Font", designInfo.font],
                    ["Logo file", designInfo.logoFileName],
                    ["Banner file", designInfo.bannerFileName],
                  ]}
                />
                <SubmissionSection
                  title="5. Products"
                  icon="fa-tags"
                  entries={productEntries.length ? productEntries : [["Products added", "None"]]}
                />
                <SubmissionSection
                  title="6. Agreement"
                  icon="fa-file-signature"
                  entries={[
                    ["Accepted", agreementInfo.accepted ?? request.AgreementAccepted],
                    ["Agreed at", agreementInfo.agreedAt || requirements.contractAcceptance?.acceptedAt],
                    ["Agreement number", agreementInfo.contract?.agreementNumber || requirements.contractAcceptance?.agreementNumber],
                  ]}
                />
                <SubmissionSection
                  title="7. Invoice & payment"
                  icon="fa-file-invoice-dollar"
                  entries={[
                    ["Invoice number", invoiceInfo.invoiceNumber],
                    [
                      "Selected package",
                      invoiceInfo.packageName ||
                        requirements.package?.name ||
                        request.PackageName,
                    ],
                    [
                      "Package charge due now",
                      invoiceInfo.packageAmount !== undefined
                        ? `R${Number(invoiceInfo.packageAmount).toFixed(2)}`
                        : "",
                    ],
                    [
                      "Package setup fee",
                      invoiceInfo.packageSetupFee !== undefined
                        ? `R${Number(invoiceInfo.packageSetupFee).toFixed(2)}`
                        : "",
                    ],
                    [
                      "Domain list price",
                      invoiceInfo.domainListPrice !== undefined
                        ? `R${Number(invoiceInfo.domainListPrice).toFixed(2)}`
                        : "",
                    ],
                    [
                      "Included domain allowance",
                      invoiceInfo.domainAllowance !== undefined
                        ? `-R${Number(invoiceInfo.domainAllowance).toFixed(2)}`
                        : "",
                    ],
                    [
                      "Domain total",
                      invoiceInfo.domainTotal !== undefined
                        ? `R${Number(invoiceInfo.domainTotal).toFixed(2)}`
                        : "",
                    ],
                    [
                      "Total due",
                      invoiceInfo.totalDue !== undefined
                        ? `R${Number(invoiceInfo.totalDue).toFixed(2)}`
                        : "",
                    ],
                    ["Payment method", request.LatestPaymentMethod || invoiceInfo.paymentMethod],
                    ["Payment status", request.PaymentStatus],
                    ["Payment reference", request.LatestPaymentReference],
                  ]}
                />
              </>
            ) : null}
          </div>
        </section>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading requests...</p>
      </div>
    );
  }

  return (
    <div className="admin-request-list-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2>Store Requests</h2>
        <span style={{ color: "var(--text-light)" }}>
          Total: {requests.length}
        </span>
      </div>

      {/* Filters */}
      <div className="dashboard-card" style={{ marginBottom: "24px" }}>
        <form
          onSubmit={handleSearch}
          style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
        >
          <div style={{ flex: 1, minWidth: "200px" }}>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by business name or request #"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "2px solid #e0e0e0",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ minWidth: "150px" }}>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "2px solid #e0e0e0",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="in_progress">In Progress</option>
              <option value="ready_for_review">Ready for Review</option>
              <option value="live">Live</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <button
            type="submit"
            className="btn-primary"
            style={{ width: "auto", padding: "8px 24px" }}
          >
            Search
          </button>
          <button
            type="button"
            className="btn-secondary"
            style={{ width: "auto", padding: "8px 24px" }}
            onClick={() => {
              setFilters({ status: "", search: "" });
              setSearchParams({});
            }}
          >
            Clear
          </button>
        </form>
      </div>

      {/* Requests Table */}
      <div className="dashboard-card" style={{ overflow: "auto" }}>
        {requests.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "var(--text-light)",
            }}
          >
            <p>No store requests found</p>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                <th style={{ padding: "12px", textAlign: "left" }}>
                  Request #
                </th>
                <th style={{ padding: "12px", textAlign: "left" }}>Business</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Package</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Progress</th>
                <th style={{ padding: "12px", textAlign: "left" }}>
                  Submitted
                </th>
                <th style={{ padding: "12px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr
                  key={req.StoreRequestID}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td style={{ padding: "12px", fontWeight: "500" }}>
                    {req.RequestNumber}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div>
                      <strong>{req.ClientBusinessName}</strong>
                    </div>
                    <div
                      style={{ fontSize: "13px", color: "var(--text-light)" }}
                    >
                      {req.Email}
                    </div>
                  </td>
                  <td style={{ padding: "12px" }}>{req.PackageName}</td>
                  <td style={{ padding: "12px" }}>
                    <span className={getStatusBadge(req.Status)}>
                      {req.Status?.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    {req.TotalSteps > 0 ? (
                      <div>
                        <div
                          className="progress-bar"
                          style={{ width: "100px" }}
                        >
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Math.round((req.CompletedSteps / req.TotalSteps) * 100)}%`,
                            }}
                          ></div>
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "var(--text-light)",
                          }}
                        >
                          {Math.round(
                            (req.CompletedSteps / req.TotalSteps) * 100,
                          )}
                          %
                        </span>
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px" }}>
                    {formatDate(req.SubmittedDate)}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div
                      className="request-row-actions"
                      data-request-actions
                    >
                      <button
                        className="request-actions-trigger"
                        type="button"
                        aria-label={`More actions for ${req.BusinessName || "store request"}`}
                        aria-haspopup="menu"
                        aria-expanded={openActionsRequestId === req.StoreRequestID}
                        title="More actions"
                        onClick={() =>
                          setOpenActionsRequestId((currentId) =>
                            currentId === req.StoreRequestID
                              ? null
                              : req.StoreRequestID,
                          )
                        }
                      >
                        <MoreHorizontal size={19} />
                      </button>
                      {openActionsRequestId === req.StoreRequestID && (
                        <div
                          className="request-actions-popup"
                          role="menu"
                          data-request-actions
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpenActionsRequestId(null);
                              openSubmissionModal(req.StoreRequestID);
                            }}
                          >
                            <Eye size={15} />
                            View submission
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => manageRequest(req.StoreRequestID)}
                          >
                            <ExternalLink size={15} />
                            Manage request
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="delete-menu-item"
                            disabled={deletingRequestId === req.StoreRequestID}
                            onClick={() => {
                              setOpenActionsRequestId(null);
                              setActionError("");
                              setRequestToDelete(req);
                            }}
                          >
                            <Trash2 size={15} />
                            Delete request
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {renderSubmissionModal()}
      {requestToDelete && (
        <Modal
          isOpen
          onClose={() => {
            if (!deletingRequestId) setRequestToDelete(null);
          }}
          title="Permanently delete store request?"
          closeOnOverlayClick={!deletingRequestId}
        >
          <div className="request-delete-confirmation">
            <div className="request-delete-warning">
              <AlertTriangle size={24} />
              <div>
                <strong>This action cannot be undone.</strong>
                <p>
                  Permanently delete{" "}
                  <strong>
                    {requestToDelete.BusinessName || "this store request"}
                  </strong>{" "}
                  ({requestToDelete.RequestNumber || "no request number"}) and
                  its messages, onboarding progress, domain records, payments,
                  reminders, notifications, and request activity records? The
                  client account and separate support conversations will remain.
                </p>
              </div>
            </div>
            {actionError && (
              <p className="request-list-action-error" role="alert">
                {actionError}
              </p>
            )}
            <div className="request-delete-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRequestToDelete(null)}
                disabled={Boolean(deletingRequestId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger-solid"
                onClick={confirmDeleteRequest}
                disabled={Boolean(deletingRequestId)}
              >
                <Trash2 size={15} />
                {deletingRequestId ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RequestList;
